import React, { useEffect, useMemo, useState } from 'react';
import { FaSun, FaMoon, FaArrowLeft } from 'react-icons/fa';
import { IoSend } from 'react-icons/io5';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

const palette = {
  bgDark: '#0e1015',
  cardDark: '#1a1d2e',
  borderDark: '#273043',
  textDark: '#ffffff',
  subDark: '#cbd5e1',
  textLight: '#0f172a',
  subLight: '#475569',
  primary: '#2563eb',
  error: '#ef4444',
  warn: '#f59e0b',
  success: '#16a34a',
};

const MIN_LEN = 40;

export default function RevisionRequestPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const planId =
    location?.state?.planId ||
    params?.planId ||
    new URLSearchParams(location.search).get('planId') ||
    '';

  const [dark, setDark] = useState(true);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const colors = useMemo(
    () => ({
      bg: dark ? palette.bgDark : '#ffffff',
      card: dark ? palette.cardDark : '#ffffff',
      border: dark ? palette.borderDark : '#e5e7eb',
      text: dark ? palette.textDark : palette.textLight,
      sub: dark ? palette.subDark : palette.subLight,
      inputBg: dark ? '#0b1220' : '#ffffff',
      headerBtnBg: dark ? '#141827' : '#e5e7eb',
      cancelBg: dark ? '#334155' : '#e5e7eb',
    }),
    [dark]
  );

  useEffect(() => {
    try {
      const stored = localStorage.getItem('theme.dark');
      if (stored !== null) {
        setDark(stored === 'true');
      }
    } catch {}
  }, []);

  const remaining = MIN_LEN - message.trim().length;
  const canSubmit = !submitting && !!planId && message.trim().length >= MIN_LEN;

  const buildHelpText = () => {
    return (
      'بررسی درخواست ممکن است تا ۲۴ ساعت زمان ببرد.\n' +
      'تا زمان اصلاح قطعی برنامه، برنامه‌ی قبلی در دسترس می‌باشد.\n' +
      'در صورت رد اصلاح برنامه، دلیل آن در داشبورد قابل مشاهده می‌باشد.\n' +
      'دقیق و کوتاه توضیح دهید چه چیزی باید تغییر کند.\n' +
      'مثال‌ها:\n' +
      '• «زانو درد دارم؛ اسکوات و لانج حذف شود.»\n' +
      '• «تجهیزاتم فقط دمبل است؛ حرکات دستگاهی حذف شود.»\n' +
      '• «زمان تمرین ۴۵ دقیقه؛ حجم تمرین کمتر شود.»\n\n' +
      'نکته: این درخواست فقط برای اصلاح همین برنامه است.'
    );
  };

  const handleThemeToggle = () => {
    const next = !dark;
    setDark(next);
    try {
      localStorage.setItem('theme.dark', next ? 'true' : 'false');
    } catch {}
  };

  const handleSubmit = async () => {
    try {
      setError('');

      if (!planId) {
        setError('شناسه برنامه نامعتبر است.');
        return;
      }

      const trimmed = message.trim();
      if (trimmed.length < MIN_LEN) {
        setError(`حداقل ${MIN_LEN} کاراکتر لازم است.`);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('ابتدا وارد حساب کاربری شوید.');
        return;
      }

      setSubmitting(true);

      const body = {
        Message: trimmed,
        PlanRequestId: planId,
      };

      const res = await fetch(`${API_URL}/api/revision/plan/${planId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = data?.Message || 'ثبت درخواست با خطا مواجه شد.';
        setError(msg);
        return;
      }

      alert('درخواست اصلاح ثبت شد. نتیجه از همین بخش تاریخچه قابل مشاهده است و تا ۲۴ ساعت اعلام می‌شود.');
      navigate(-1);
    } catch (e) {
      console.error('submit revision error', e);
      setError('خطا در ارتباط با سرور');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen font-vazir"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          direction: 'rtl',
        }}
      >
        <div className="max-w-3xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between py-2 mb-4">
            <h1 className="text-lg md:text-xl font-bold">درخواست اصلاح</h1>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleThemeToggle}
                className="p-2 rounded-full border transition"
                style={{
                  backgroundColor: colors.headerBtnBg,
                  borderColor: colors.border,
                }}
                aria-label="تغییر تم"
              >
                {dark ? (
                  <FaSun size={18} color="#fde047" />
                ) : (
                  <FaMoon size={18} color="#0f172a" />
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-2 rounded-full border transition"
                style={{
                  backgroundColor: colors.headerBtnBg,
                  borderColor: colors.border,
                }}
                aria-label="بازگشت"
              >
                <FaArrowLeft size={18} color={dark ? '#fff' : '#0f172a'} />
              </button>
            </div>
          </div>

          <div
            className="rounded-2xl border p-4 md:p-5"
            style={{
              backgroundColor: colors.card,
              borderColor: colors.border,
            }}
          >
            <p
              className="text-sm leading-8 text-right whitespace-pre-line mb-4"
              style={{ color: colors.sub }}
            >
              {buildHelpText()}
            </p>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="توضیح دقیق تغییرات موردنظر…"
              disabled={submitting}
              className="w-full rounded-xl border p-3 outline-none resize-y"
              style={{
                minHeight: 160,
                borderColor: colors.border,
                color: colors.text,
                backgroundColor: colors.inputBg,
                textAlign: 'right',
                direction: 'rtl',
              }}
            />

            <div className="mt-2 flex items-center justify-between flex-row-reverse">
              <span className="text-xs" style={{ color: colors.sub }}>
                {remaining > 0 ? `حداقل ${remaining} کاراکتر دیگر…` : 'آماده ارسال'}
              </span>

              <span className="text-xs" style={{ color: colors.sub }}>
                {message.trim().length}/{MIN_LEN}
              </span>
            </div>

            {error ? (
              <p
                className="mt-3 text-sm text-right"
                style={{ color: palette.error }}
              >
                {error}
              </p>
            ) : null}

            {!planId ? (
              <p
                className="mt-3 text-sm text-right"
                style={{ color: palette.error }}
              >
                شناسه برنامه پیدا نشد.
              </p>
            ) : null}

            <div className="flex flex-row-reverse gap-2 mt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={submitting}
                className="px-4 py-2.5 rounded-xl border text-sm transition"
                style={{
                  backgroundColor: colors.cancelBg,
                  borderColor: colors.border,
                  color: colors.text,
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                انصراف
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition"
                style={{
                  backgroundColor: canSubmit ? palette.warn : '#475569',
                  color: '#fff',
                  opacity: canSubmit ? 1 : 0.6,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {submitting ? (
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <IoSend size={16} color="#fff" />
                )}
                <span>ثبت درخواست</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}